// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getProp, resolveColor } from "@insite/mobius/utilities";
import { HEADER_HEIGHT_CSS_VAR } from "@stoneage/XDL/constants";
import { css } from "styled-components";

/**
 * Add custom style for scrollbar
 * @param width
 * @param thumbColorProps
 */
export const styledScrollbar = (width = 5, thumbColorProps?: string) => {
    const backgroundColor = "transparent";
    const thumbColor = thumbColorProps ?? "common.ui6";
    const thumbHoverColor = "common.ui7";

    return ({ theme }: any) => css`
        overflow-y: auto;
        transition: all ${getProp("theme.transition.duration.regular")}ms ease-in-out;

        @supports not selector(::-webkit-scrollbar) {
            scrollbar-width: thin;
            scrollbar-color: ${resolveColor(thumbColor, theme)} ${resolveColor(backgroundColor, theme)};
        }

        &::-webkit-scrollbar {
            width: ${width}px;
            height: ${width}px;
        }
        &::-webkit-scrollbar-track {
            background: ${resolveColor(backgroundColor, theme)};
        }
        &::-webkit-scrollbar-thumb {
            background-color: ${resolveColor(thumbColor, theme)};
            border-radius: 6px;
            border: 3px solid transparent;
            transition: background-color ease-in-out ${getProp("theme.transition.duration.regular")}ms;
        }
        &::-webkit-scrollbar-thumb:hover {
            background-color: ${resolveColor(thumbHoverColor, theme)};
        }
    `;
};

export const hideScrollBar = css`
    &::-webkit-scrollbar {
        display: none;
    }
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */

    /* this prevent scroll outside the container */
    overscroll-behavior-y: contain;
`;

const hasVScroll = () => document.body.scrollHeight > document.body.clientHeight;

export const addScrollbarGutter = () => {
    if (typeof document === "undefined") {
        return;
    }

    if (!hasVScroll()) {
        return;
    }

    document.querySelector("html")?.style.setProperty("scrollbar-gutter", "stable");
};

export const removeScrollbarGutter = () => {
    if (typeof document === "undefined") {
        return;
    }

    if (!hasVScroll()) {
        return;
    }

    document.querySelector("html")?.style.removeProperty("scrollbar-gutter");
};

/**
 * Scroll to invalid field
 * @param scrollToTop
 */
export const doScroll = (scrollToTop = false) => {
    setTimeout(
        () => {
            const selector = document.querySelector("[aria-invalid='true']");

            if (selector) {
                selector.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                });

                return;
            }

            if (scrollToTop) {
                window.scrollTo({
                    top: 0,
                    left: 0,
                    behavior: "smooth",
                });
                return;
            }

            doScroll(scrollToTop);
        },
        scrollToTop ? 10 : 500,
    );
};

/* -------------------------------------------------------------------------- */
/* XNG-Changes: category anchor deep-linking                                   */
/* Used by the mega menu "View All" link, the category banner sub-category     */
/* links and the category level sections they scroll to.                       */
/* -------------------------------------------------------------------------- */

export const categoryAnchorPrefix = "category-link-";

/** Builds the dom id used as the scroll target for a category section. */
export const getCategoryAnchorId = (categoryId?: string) =>
    categoryId ? `${categoryAnchorPrefix}${categoryId}` : "";

/** True when a hash (with or without "#") points at a category section. */
export const isCategoryAnchor = (hash?: string) =>
    !!hash && hash.replace("#", "").startsWith(categoryAnchorPrefix);

/**
 * Sticky header compensation. Falls back to the --headerHeight css variable so
 * callers outside of the redux-connected components don't need the store.
 */
export const getHeaderOffset = (headerHeight?: number) => {
    if (headerHeight && headerHeight > 0) {
        return headerHeight + 20;
    }

    if (typeof document === "undefined") {
        return 0;
    }

    const parsed = parseInt(
        getComputedStyle(document.documentElement).getPropertyValue(HEADER_HEIGHT_CSS_VAR).trim(),
        10,
    );

    return !parsed || Number.isNaN(parsed) || parsed <= 0 ? 0 : parsed + 20;
};

/** Scrolls an element into view under the sticky header. */
export const scrollToCategoryElement = (element: HTMLElement, headerHeight?: number) => {
    window.scrollTo({
        top: element.getBoundingClientRect().top + window.scrollY - getHeaderOffset(headerHeight),
        behavior: "smooth",
    });
};

/** Scrolls to a category anchor if it is already in the dom. Returns whether it scrolled. */
export const scrollToCategoryAnchor = (anchorId: string, headerHeight?: number) => {
    if (typeof document === "undefined" || !anchorId) {
        return false;
    }

    const element = document.getElementById(anchorId.replace("#", ""));
    if (!element) {
        return false;
    }

    scrollToCategoryElement(element, headerHeight);
    return true;
};

/**
 * Scrolls to a category anchor, waiting for it to render first — category data
 * loads asynchronously, so the target usually isn't in the dom on first paint.
 * A single corrective scroll runs once the smooth scroll settles, because lazy
 * images above the anchor can shift the layout mid-flight.
 * Returns a cancel function; any user scroll/keyboard input cancels it too.
 */
export const scrollToCategoryAnchorWhenReady = (anchorId: string, headerHeight?: number, timeoutMs = 5000) => {
    const noop = () => {};

    if (typeof window === "undefined" || !anchorId) {
        return noop;
    }

    const id = anchorId.replace("#", "");
    const startedAt = typeof performance !== "undefined" ? performance.now() : 0;
    const userEvents = ["wheel", "touchstart", "keydown"];

    let frame = 0;
    let settleTimeout = 0;
    let cancelled = false;

    const cancel = () => {
        if (cancelled) {
            return;
        }
        cancelled = true;
        cancelAnimationFrame(frame);
        clearTimeout(settleTimeout);
        userEvents.forEach(eventName => window.removeEventListener(eventName, cancel));
    };

    const attempt = () => {
        if (cancelled) {
            return;
        }

        if (scrollToCategoryAnchor(id, headerHeight)) {
            // Re-align after the smooth scroll settles in case the layout moved.
            settleTimeout = window.setTimeout(() => {
                if (!cancelled) {
                    scrollToCategoryAnchor(id, headerHeight);
                }
                cancel();
            }, 700);
            return;
        }

        const elapsed = (typeof performance !== "undefined" ? performance.now() : 0) - startedAt;
        if (elapsed < timeoutMs) {
            frame = requestAnimationFrame(attempt);
            return;
        }

        cancel();
    };

    userEvents.forEach(eventName => window.addEventListener(eventName, cancel, { passive: true }));
    frame = requestAnimationFrame(attempt);

    return cancel;
};

/** Comparable form of a url path — query/hash stripped, decoded, lowercased, no trailing slash. */
export const toComparablePath = (path: string) => {
    const withoutQuery = path.split(/[?#]/)[0].replace(/\/+$/, "") || "/";

    try {
        return decodeURIComponent(withoutQuery).toLowerCase();
    } catch {
        return withoutQuery.toLowerCase();
    }
};

/** True when the href targets the page the user is already on (ignoring query and hash). */
export const isSamePagePath = (href?: string) =>
    !!href && typeof window !== "undefined" && toComparablePath(href) === toComparablePath(window.location.pathname);

/** Left click with no modifier keys — i.e. a click we're allowed to hijack. */
export const isModifiedClick = (event: { button?: number; metaKey?: boolean; ctrlKey?: boolean; shiftKey?: boolean; altKey?: boolean }) =>
    (event.button ?? 0) !== 0 || !!event.metaKey || !!event.ctrlKey || !!event.shiftKey || !!event.altKey;

/** Updates the hash without pushing a history entry. Pass nothing to drop the hash. */
export const replaceLocationHash = (anchorId?: string) => {
    if (typeof window === "undefined" || !window.history?.replaceState) {
        return;
    }

    // Always build an absolute-path url: a bare "#hash" is resolved against any
    // <base href> in the document, which silently drops the current path.
    const base = `${window.location.pathname}${window.location.search}`;
    const url = anchorId ? `${base}#${anchorId.replace("#", "")}` : base;

    window.history.replaceState(null, "", url);
};

/**
 * Scrolls the page back to the top. Re-issued on the next frame because the
 * router may re-render (and reset scroll handling) in the same tick.
 */
export const scrollPageToTop = () => {
    if (typeof window === "undefined") {
        return;
    }

    const toTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

    toTop();
    requestAnimationFrame(toTop);
};
